import { CrudBuilderReturnType, GetParams, GetParamsWithSpace } from "utils/client/CRUD/types";
import { Dispatch, SetStateAction, ReactNode, HTMLAttributes, SyntheticEvent } from "react";
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteOwnerState,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  TextFieldProps,
} from "@mui/material";

export type GetParamsWithoutGeneric = Omit<GetParams<any>, "T">;

export interface Option {
  id: string;
  autocompleteLabel: string;
  [key: string]: any;
}

export interface Dependency {
  /** Whether the dependency is fulfilled */
  fulfilled: boolean;

  /** Whether this field should be disabled when dependency is NOT fulfilled */
  disabledWhenUnfulfilled: boolean;

  /** We need to reset options based on dependency - set this value every time dependency changes */
  dependencyValue?: any;

  // If disabled because of a missing dependency - a tooltip can be used as an indicator for the user why
  tooltipMessage?: string;
}

export type CustomAutocompleteOnChange = (
  event: SyntheticEvent<Element, Event> | null,
  value: any,
  reason: AutocompleteChangeReason,
  details?: AutocompleteChangeDetails<any> | undefined,
) => void;

export type CustomConnectionAutocompleteProps<T extends string, P extends boolean> = {
  /** If you would like to pass in initial options, you can. It is not necessary as connections and values will be stored within the ConnectionAutocomplete */
  additionalOptions?: AutocompleteProps<any, boolean, boolean, boolean>["options"];

  /** A function that returns a crud (for allowing you to use generics) - Ex. () => userCrud */
  crud: () => CrudBuilderReturnType<T, P>;

  /** If the retrieve function fails - the error will be passed to this function to be handled as needed */
  retrieveErrorHandler?: (error: any) => void;

  /** An array of the fields you want searched when typed - usually one field but multiple is necessary for instances like firstName and lastName */
  searchFields: string[];

  /** The initial/default params to use for the crud.get - this should include things like space if needed and any default filters required */
  defaultGetParams: P extends true ? GetParams<T> : GetParamsWithSpace<T>;

  /** The label of the response from this connection's API route - Ex. "users" for the userCrud */
  responseData: string;

  /** A method to convert a single record to an option for the Autocomplete - will be used for records.map(convertRecordToOption) */
  convertRecordToOption: (record: any) => Option;

  /** The ability to store the connections retrieved from this method in state in the parent component */
  setConnections?: Dispatch<SetStateAction<any>>;

  /** The ability to store the total record count in state in the parent component */
  setTotalCount?: Dispatch<SetStateAction<number>>;

  /** When the connection is dependent upon something else - Ex. "Billing Address" should be disabled with no options until a "Billing Customer" has been selected */
  dependency?: Dependency;

  /** If we want a tooltip for the component - you can pass in a message and it will render the ToolTip for you */
  tooltipMessage?: string | ((options: Options) => string | undefined);

  /** The ability to filter the options - different than the default filterOptions on Autocomplete as this is custom and only for filtering the records */
  filterOptions?: (options: Options) => Options;

  /** Wait until the component is opened (clicked to view options) before retrieving initial records - essentially only retrieving when it is used/accessed */
  lazyRetrieve?: boolean;

  /** The ability to have the top option be "Select All" and when using `multiple`, it will allow you to select/deselect all */
  selectAllOption?: boolean;

  /** Custom render option to be able to pass additional props */
  renderOption?: CustomRenderOption;
};

// export type GenericCustomConnectionAutocompleteProps = CustomConnectionAutocompleteProps<GetParamsWithoutGeneric>;

/**
    @description Use Autocomplete as normal but with additional props for the connections
*/

export type ConnectionAutocompleteProps<T extends string, P extends boolean> = Omit<
  AutocompleteProps<any, boolean, boolean, boolean>,
  "onChange" | "options" | "isOptionEqualToValue" | "getOptionLabel" | "filterOptions" | "renderOption"
> &
  CustomConnectionAutocompleteProps<T, P> & {
    /** Override the onChange to allow the event to be null */
    onChange: CustomAutocompleteOnChange;
  };

export type Options = AutocompleteProps<any, boolean, boolean, boolean>["options"];

export interface ConnectionAutocompleteRef {
  options: Options;
  loading: boolean;
}

export interface GenerateRetrieveConnectionsParams<T extends GetParamsWithoutGeneric> {
  crud: CrudBuilderReturnType<string, boolean>;
  haveRetrievedAll: boolean;
  setHaveRetrievedAll: Dispatch<SetStateAction<boolean>>;
  defaultGetParams: T;
  inputValue: string;
  searchFields: string[];
  responseData: string;
  convertRecordToOption: (record: any) => Option;
  dispatchOptions: Dispatch<OptionsAction>;
  value: any;
  setLoading: Dispatch<SetStateAction<boolean>>;
  options: Options;
  setTotalRecords: Dispatch<SetStateAction<number>>;
  retrieveErrorHandler?: (error: any) => void;
}

export type RetrieveConnectionsRequestParams<T extends GetParamsWithoutGeneric> = {
  performingSearch: boolean;
  searchFields: string[];
  inputValue: string;
  defaultGetParams: T;
  crud: CrudBuilderReturnType<string, boolean>;
  defaultRetrievalAmount: number;
  exactMatch: boolean;
};

export interface CombineOptionsWithValueParams {
  options: Options;
  value: any;
}

export interface FormatOptionsWithRecordConverterParams {
  options: Options;
  convertRecordToOption: (record: any) => Option;
}

export interface CalculateValueParams {
  value: any;
  convertRecordToOption: (record: any) => Option;
}

export interface CustomRenderInputParams {
  renderInput: (params: AutocompleteRenderInputParams) => ReactNode;
  loading: boolean;
  loadingProp: boolean;
  dependency?: Dependency;
  tooltipMessage?: string;
  haveRetrievedAll: boolean;
  value: any;
  disabled: boolean;
}

export type CustomRenderOption = (
  props: RenderOptionLIElement,
  option: any,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<any, boolean, boolean, boolean, "div">,
  isAllSelected: boolean,
) => ReactNode;

export interface GenerateRenderOptionParams {
  renderOption?: CustomRenderOption;
  isAllSelected: boolean;
}

export type RenderOptionLIElement = HTMLAttributes<HTMLLIElement> & { key: string };

export interface GenerateOptionsReducerParams {
  convertRecordToOption: (record: any) => Option;
}

export interface RetrieveConnectionsParams {
  dependencyValueChange: boolean;
  dependencyFulfilled?: boolean;
}

export enum OptionsActionTypes {
  REPLACE = "Replace",
  ADD = "Add",
}

export interface ReplaceOptions {
  type: OptionsActionTypes.REPLACE;
  payload: Options;
}

export interface AddOptions {
  type: OptionsActionTypes.ADD;
  payload: Options;
}

export type OptionsAction = ReplaceOptions | AddOptions;

export interface GenerateOnChangeParams {
  onChange: AutocompleteProps<any, boolean, boolean, boolean>["onChange"];
  value: any;
  dispatchOptions: Dispatch<OptionsAction>;
  options: Option[];
  isAllSelected: boolean;
  selectAllOption: boolean;
}

export interface GenerateFilterOptionsParams {
  filterOptions: AutocompleteProps<any, boolean, boolean, boolean>["filterOptions"];
}
